import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../config/constants";
import { getAge } from "../../../config/functions";
import { GetProtocolData } from "../sections/getProtocolData";
import { localizationMapping, evaluationPercentageKeys, activityIds, islandId, capsuleIds } from "../functions";
import StatisticsModule from "../functions/statistics";
// Components
import { GroupFormsContainer, EvaluationDataDiv, Column, Row, Box, Text, Separator } from "./styles";
import { ScreenshotContainer } from "../sections/generalStyles";
import Loader from "../../loader";
import UseFetch from "../../../config/useFetch";
import PatientNavigation from "../../patientNavigation";
import MenuNavigation from "../sections/menuNavigation";
import Title from "../../title";
import DownloadReportPDF from "../sections/downloadReportPDF";
import GroupForm from "./sections/groupForm";
import ActivitiesHeader from "./sections/activitiesHeader";
import ProtocolData from "./sections/protocolData";
import EvaluationsHeader from "./sections/evaluationsHeader";

const Comparison = () => {
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [patients, setPatients] = useState([]);
  const [patientsGroup1, setPatientsGroup1] = useState([]);
  const [patientsGroup2, setPatientsGroup2] = useState([]);
  const [evaluationKeys] = useState(["Dyspnoea", "Pain", "AnxiousSymptomatology", "DepressiveSymptoms"]);
  const [pValues, setPValues] = useState({});
  // Group 1
  const [gender1, setGender1] = useState("");
  const [minAge1, setMinAge1] = useState("");
  const [maxAge1, setMaxAge1] = useState("");
  const [delirium1, setDelirium1] = useState("");
  const [position1, setPosition1] = useState("");
  const [mechanicalVentilation1, setMechanicalVentilation1] = useState("");
  // Group 2
  const [isGroup2Enabled, setIsGroup2Enabled] = useState(false);
  const [gender2, setGender2] = useState("");
  const [minAge2, setMinAge2] = useState("");
  const [maxAge2, setMaxAge2] = useState("");
  const [delirium2, setDelirium2] = useState("");
  const [position2, setPosition2] = useState("");
  const [mechanicalVentilation2, setMechanicalVentilation2] = useState("");

  // Set plot images
  const activitiesRef = useRef(null);
  const evaluationsRef = useRef(null);

  const hospitalId = useSelector(({ clinician }) => clinician.data.HOSPITAL_ID);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetching Patients
  useEffect(() => {
    if (hospitalId) {
      UseFetch("get", `${WEB_SERVICE_MIMS}/icu/get-patients-sessions-and-evaluations/${hospitalId}`)
        .then(({ data }) => {
          const patientWithEvaluations = data.filter((patient) => patient.superSessions.length);
          setPatients(patientWithEvaluations);
          setPatientsGroup1(patientWithEvaluations);
          setPatientsGroup2(patientWithEvaluations);
          setIsLoadingPatients(false);
        })
        .catch((error) => {
          console.log("Get patients sessions and evaluations: ", error);
        });
    }

    return () => {
      setPatients([]);
      setPatientsGroup1([]);
      setPatientsGroup2([]);
      setIsLoadingPatients(true);
    };
  }, [hospitalId]);

  const cloneArray = (array) => JSON.parse(JSON.stringify(array));

  const filterByGender = (filteredPatients, gender) => {
    return filteredPatients.filter((patient) => patient.GENDER === gender);
  };

  const filterByAge = (filteredPatients, age, isMinAge) => {
    const ageToFilter = Number(age);
    return filteredPatients.filter((patient) => {
      if (isNaN(ageToFilter)) return false;
      const patientAge = getAge(patient.BIRTH_DATE);
      return isMinAge ? patientAge >= ageToFilter : patientAge <= ageToFilter;
    });
  };

  const filterByEvaluation = (filteredPatients, evaluation, evaluationGroup) => {
    return filteredPatients.map((patient) => {
      patient.superSessions = patient.superSessions.filter((session) => {
        return session.evaluations[0][evaluation] === evaluationGroup;
      });
      return patient;
    });
  };

  const renderGroupForms = () => {
    const updateDataForGroup1 = (e) => {
      e.preventDefault();
      let filteredPatients = cloneArray(patients);

      if (gender1) filteredPatients = filterByGender(filteredPatients, gender1);
      if (minAge1) filteredPatients = filterByAge(filteredPatients, minAge1, true);
      if (maxAge1) filteredPatients = filterByAge(filteredPatients, maxAge1, false);
      if (delirium1) filteredPatients = filterByEvaluation(filteredPatients, "Delirium", delirium1);
      if (position1) filteredPatients = filterByEvaluation(filteredPatients, "Position", position1);
      if (mechanicalVentilation1) filteredPatients = filterByEvaluation(filteredPatients, "MechanicalVentilation", mechanicalVentilation1);

      const patientsWithSuperSessions = filteredPatients.filter((patient) => patient.superSessions.length);
      setPatientsGroup1(patientsWithSuperSessions);
    };

    const updateDataForGroup2 = (e) => {
      e.preventDefault();
      let filteredPatients = cloneArray(patients);

      if (gender2) filteredPatients = filterByGender(filteredPatients, gender2);
      if (minAge2) filteredPatients = filterByAge(filteredPatients, minAge2, true);
      if (maxAge2) filteredPatients = filterByAge(filteredPatients, maxAge2, false);
      if (delirium2) filteredPatients = filterByEvaluation(filteredPatients, "Delirium", delirium2);
      if (position2) filteredPatients = filterByEvaluation(filteredPatients, "Position", position2);
      if (mechanicalVentilation2) filteredPatients = filterByEvaluation(filteredPatients, "MechanicalVentilation", mechanicalVentilation2);

      const patientsWithSuperSessions = filteredPatients.filter((patient) => patient.superSessions.length);
      setPatientsGroup2(patientsWithSuperSessions);
    };

    return (
      <GroupFormsContainer>
        <GroupForm
          isGroup1
          gender={gender1}
          setGender={setGender1}
          minAge={minAge1}
          setMinAge={setMinAge1}
          maxAge={maxAge1}
          setMaxAge={setMaxAge1}
          delirium={delirium1}
          setDelirium={setDelirium1}
          position={position1}
          setPosition={setPosition1}
          mechanicalVentilation={mechanicalVentilation1}
          setMechanicalVentilation={setMechanicalVentilation1}
          filterDataEvent={updateDataForGroup1}
        />
        <GroupForm
          isGroup2Enabled={isGroup2Enabled}
          setIsGroup2Enabled={setIsGroup2Enabled}
          gender={gender2}
          setGender={setGender2}
          minAge={minAge2}
          setMinAge={setMinAge2}
          maxAge={maxAge2}
          setMaxAge={setMaxAge2}
          delirium={delirium2}
          setDelirium={setDelirium2}
          position={position2}
          setPosition={setPosition2}
          mechanicalVentilation={mechanicalVentilation2}
          setMechanicalVentilation={setMechanicalVentilation2}
          filterDataEvent={updateDataForGroup2}
        />
      </GroupFormsContainer>
    );
  };

  const filterSessionsByGroup = (patientsGroup, protocolId) => {
    const groupSessions = patientsGroup.flatMap((patient) => patient.superSessions.flatMap((superSession) => superSession.sessions));
    return groupSessions.filter((session) => session.PROTOCOL_ID === protocolId);
  };

  const renderProtocolData = (title, protocolIds, showDetailsText) => {
    return (
      <>
        <ActivitiesHeader title={title} showDetailsText={showDetailsText} />
        <>
          {protocolIds.map((protocolId) => {
            const { img, name } = GetProtocolData(protocolId);
            const protocolSessionsGroup1 = filterSessionsByGroup(patientsGroup1, protocolId);
            const protocolSessionsGroup2 = filterSessionsByGroup(patientsGroup2, protocolId);

            return (
              <ProtocolData
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                protocolVideo={t(`protocols_${name}Video`)}
                protocolDescription={t(`protocols_${name}Description`)}
                sessionsGroup1={protocolSessionsGroup1}
                sessionsGroup2={protocolSessionsGroup2}
                isGroup2Enabled={isGroup2Enabled}
              />
            );
          })}
        </>
      </>
    );
  };

  const getEvaluationsPerGroupAndPosition = (patientsGroup, position) => {
    return patientsGroup.flatMap((patient) =>
      patient.superSessions.flatMap((superSession) =>
        superSession.evaluations.length > position ? superSession.evaluations[position] : {}
      )
    );
  };

  const getAveragePerGroupAndKey = (patientsGroup, key, position) => {
    const groupEvaluations = getEvaluationsPerGroupAndPosition(patientsGroup, position);
    const sumValues = groupEvaluations.reduce((count, evaluation) => count + Number(evaluation[key]), 0);
    const average = !groupEvaluations.length ? 0 : sumValues / groupEvaluations.length;
    return !average ? "-" : average.toString().length > 4 ? average.toFixed(2) : average;
  };

  const getValueDifference = (value1, value2) => {
    if (isNaN(value1) && isNaN(value2)) return "-";
    if (isNaN(value1)) return `+ ${value2}`;
    if (isNaN(value2)) return `- ${value1}`;

    const difference = value2 - value1;
    const parsedDifference = difference.toString().length > 4 ? Number(difference.toFixed(2)) : difference;

    if (parsedDifference < 0) return `- ${Math.abs(parsedDifference)}`;
    if (parsedDifference > 0) return `+ ${parsedDifference}`;

    return parsedDifference;
  };

  const parsePValue = (pValue) => (isNaN(pValue) ? "-" : pValue.toString().length > 5 ? pValue.toFixed(2) : pValue);

  const renderSleepEvaluation = () => {
    const sleepAverageGroup1 = getAveragePerGroupAndKey(patientsGroup1, "Sleep", 0);
    const sleepAverageGroup2 = getAveragePerGroupAndKey(patientsGroup2, "Sleep", 0);
    const averageDifference = getValueDifference(sleepAverageGroup1, sleepAverageGroup2);
    const pValue = parsePValue(pValues?.Sleep?.pValue);

    return (
      <EvaluationDataDiv>
        <Row background="#F2F2F2F2">
          <Box width="15%" />
          <Box borderLeft={"2px solid #fff"}>
            <Text strong align="center">
              {t("average")}
            </Text>
          </Box>
          {isGroup2Enabled && (
            <>
              <Box>
                <Text strong align="center">
                  {t("difference")}
                </Text>
              </Box>
              <Box>
                <Text strong align="center">
                  {t("pValue")}
                </Text>
              </Box>
            </>
          )}
        </Row>
        <Row background="#FFF">
          <Box padding="0 16px" width="15%" justifyContent="flex-start" background="#F2F2F2F2">
            <Text strong>{t("Sleep")}</Text>
          </Box>
          <Box>
            <Text width="40px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
              {sleepAverageGroup1}
            </Text>
            {isGroup2Enabled && (
              <>
                <Separator background="#808080" />
                <Text width="40px" color="#F15A24" align="left">
                  {sleepAverageGroup2}
                </Text>
              </>
            )}
          </Box>
          {isGroup2Enabled && (
            <>
              <Box>
                <Text align="center">{averageDifference}</Text>
              </Box>
              <Box>
                <Text align="center">{pValue}</Text>
              </Box>
            </>
          )}
        </Row>
      </EvaluationDataDiv>
    );
  };

  const renderEvaluationsDataAverage = () => {
    const renderKeys = () => {
      return (
        <Column width="15%" background="#F2F2F2F2">
          <Box />
          {evaluationKeys.map((key) => {
            return (
              <Box key={key} padding="0 16px" justifyContent="flex-start" background="#F2F2F2F2">
                <Text strong>{t(key)}</Text>
              </Box>
            );
          })}
        </Column>
      );
    };

    const renderEvaluationsData = () => {
      const pValuesMapping = {
        Dyspnoea: pValues.Dyspnoea,
        Pain: pValues.Pain,
        AnxiousSymptomatology: pValues.Worry,
        DepressiveSymptoms: pValues.Sadness,
      };

      const boxSize = "145px";
      return (
        <Column width="85%" overFlowX="auto">
          <Row>
            <Box width={isGroup2Enabled ? boxSize : 0} flexDirection="column" background="#F2F2F2F2" borderLeft={"2px solid #fff"} gap="0">
              <Text strong align="center">
                {t("average")}
              </Text>
              <Text strong align="center">
                PRE
              </Text>
            </Box>
            {isGroup2Enabled && (
              <Box width={boxSize} flexDirection="column" background="#F2F2F2F2" gap="0">
                <Text strong align="center">
                  {t("difference")}
                </Text>
                <Text strong align="center">
                  PRE
                </Text>
              </Box>
            )}
            <Box width={isGroup2Enabled ? boxSize : 0} flexDirection="column" background="#F2F2F2F2" gap="0">
              <Text strong align="center">
                {t("average")}
              </Text>
              <Text strong align="center">
                POST
              </Text>
            </Box>
            {isGroup2Enabled && (
              <Box width={boxSize} flexDirection="column" background="#F2F2F2F2" gap="0">
                <Text strong align="center">
                  {t("difference")}
                </Text>
                <Text strong align="center">
                  POST
                </Text>
              </Box>
            )}
            <Box width={isGroup2Enabled ? boxSize : 0} flexDirection="column" background="#F2F2F2F2" gap="0">
              <Text strong align="center">
                {t("difference")}
              </Text>
              <Text strong align="center">
                PRE-POST
              </Text>
            </Box>
            {!isGroup2Enabled && (
              <Box background="#F2F2F2F2">
                <Text strong align="center">
                  {t("pValue")}
                </Text>
              </Box>
            )}
            {isGroup2Enabled && (
              <>
                <Box width={boxSize} background="#F2F2F2F2">
                  <Text strong align="center">
                    {t("pValueGroup")}
                  </Text>
                </Box>
                <Box width={boxSize} background="#F2F2F2F2">
                  <Text strong align="center">
                    {t("pValueTime")}
                  </Text>
                </Box>
                <Box width={boxSize} background="#F2F2F2F2">
                  <Text strong align="center">
                    {t("pValueInteraction")}
                  </Text>
                </Box>
              </>
            )}
          </Row>
          {evaluationKeys.map((key) => {
            const averagePREGroup1 = getAveragePerGroupAndKey(patientsGroup1, key, 0);
            const averagePREGroup2 = getAveragePerGroupAndKey(patientsGroup2, key, 0);
            const averagePOSTGroup1 = getAveragePerGroupAndKey(patientsGroup1, key, 1);
            const averagePOSTGroup2 = getAveragePerGroupAndKey(patientsGroup2, key, 1);

            const averageDifferencePRE = getValueDifference(averagePREGroup1, averagePREGroup2);
            const averageDifferencePOST = getValueDifference(averagePOSTGroup1, averagePOSTGroup2);
            const averageDifferencePREPOST1 = getValueDifference(averagePREGroup1, averagePOSTGroup1);
            const averageDifferencePREPOST2 = getValueDifference(averagePREGroup2, averagePOSTGroup2);

            const pValues = pValuesMapping[key];
            const pValue = parsePValue(pValues?.pValue);
            const pValueGroup = parsePValue(pValues?.groupPValue);
            const pValueTime = parsePValue(pValues?.timePValue);
            const pValueInteraction = parsePValue(pValues?.interactionPValue);

            return (
              <Row key={key} background="#FFF">
                <Box width={isGroup2Enabled ? boxSize : 0}>
                  <Text width="30px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                    {averagePREGroup1}
                  </Text>
                  {isGroup2Enabled && (
                    <>
                      <Separator background="#808080" />
                      <Text width="30px" color="#F15A24" align="left">
                        {averagePREGroup2}
                      </Text>
                    </>
                  )}
                </Box>
                {isGroup2Enabled && (
                  <Box width={boxSize}>
                    <Text align="center">{averageDifferencePRE}</Text>
                  </Box>
                )}
                <Box width={isGroup2Enabled ? boxSize : 0}>
                  <Text width="30px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                    {averagePOSTGroup1}
                  </Text>
                  {isGroup2Enabled && (
                    <>
                      <Separator background="#808080" />
                      <Text width="30px" color="#F15A24" align="left">
                        {averagePOSTGroup2}
                      </Text>
                    </>
                  )}
                </Box>
                {isGroup2Enabled && (
                  <Box width={boxSize}>
                    <Text align="center">{averageDifferencePOST}</Text>
                  </Box>
                )}
                <Box width={isGroup2Enabled ? boxSize : 0}>
                  <Text width="40px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                    {averageDifferencePREPOST1}
                  </Text>
                  {isGroup2Enabled && (
                    <>
                      <Separator background="#808080" />
                      <Text width="40px" color="#F15A24" align="left">
                        {averageDifferencePREPOST2}
                      </Text>
                    </>
                  )}
                </Box>
                {!isGroup2Enabled && (
                  <Box>
                    <Text align="center">{pValue}</Text>
                  </Box>
                )}
                {isGroup2Enabled && (
                  <>
                    <Box width={boxSize}>
                      <Text align="center">{pValueGroup}</Text>
                    </Box>
                    <Box width={boxSize}>
                      <Text align="center">{pValueTime}</Text>
                    </Box>
                    <Box width={boxSize}>
                      <Text align="center">{pValueInteraction}</Text>
                    </Box>
                  </>
                )}
              </Row>
            );
          })}
        </Column>
      );
    };

    return (
      <EvaluationDataDiv flexDirection="row">
        {renderKeys()}
        {renderEvaluationsData()}
      </EvaluationDataDiv>
    );
  };

  const renderRASSEvaluation = () => {
    const headers = ["<= -2", "-1", "0", "1", ">= 2"];
    const cases = [-2, -1, 0, 1, 2];

    const getRASSPercentagePerGroup = (patientsGroup, value) => {
      const groupEvaluations = getEvaluationsPerGroupAndPosition(patientsGroup, 0);

      const valueCount = groupEvaluations.reduce((count, evaluation) => {
        const RASSvalue = Number(evaluation.RASS);
        if (value < -1) return count + (RASSvalue <= value ? 1 : 0);
        if (value > 1) return count + (RASSvalue >= value ? 1 : 0);
        return count + (RASSvalue === value ? 1 : 0);
      }, 0);

      const percentage = !groupEvaluations.length ? 0 : Math.round((valueCount / groupEvaluations.length) * 100);
      return !percentage ? "-" : `${percentage}%`;
    };

    return (
      <EvaluationDataDiv>
        <Row background="#F2F2F2F2">
          <Box width="15%" />
          {headers.map((header, i) => {
            return (
              <Box key={i} borderLeft={i === 0 && "2px solid #fff"}>
                <Text strong align="center">
                  {header}
                </Text>
              </Box>
            );
          })}
        </Row>
        <Row background="#FFF">
          <Box padding="0 16px" width="15%" justifyContent="flex-start" background="#F2F2F2F2">
            <Text strong>{"RASS"}</Text>
          </Box>
          {cases.map((value) => {
            return (
              <Box key={value}>
                <Text width="40px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                  {getRASSPercentagePerGroup(patientsGroup1, value)}
                </Text>
                {isGroup2Enabled && (
                  <>
                    <Separator background="#808080" />
                    <Text width="40px" color="#F15A24" align="left">
                      {getRASSPercentagePerGroup(patientsGroup2, value)}
                    </Text>
                  </>
                )}
              </Box>
            );
          })}
        </Row>
      </EvaluationDataDiv>
    );
  };

  const getValuePercentagePerGroup = (patientsGroup, key, value) => {
    const groupEvaluations = getEvaluationsPerGroupAndPosition(patientsGroup, 0);

    const valueCount = groupEvaluations.reduce((count, evaluation) => count + (evaluation[key] === value ? 1 : 0), 0);

    const percentage = !groupEvaluations.length ? 0 : Math.round((valueCount / groupEvaluations.length) * 100);
    return !percentage ? "-" : `${percentage}%`;
  };

  const renderEvaluationsDataPercentage = () => {
    return evaluationPercentageKeys.map((evaluation) => {
      const title = evaluation.key;
      const evaluationValues = evaluation.values;
      return (
        <EvaluationDataDiv key={title}>
          <Row background="#F2F2F2F2">
            <Box width="15%" />
            {evaluationValues.map((value, i) => {
              return (
                <Box key={value} borderLeft={i === 0 && "2px solid #fff"}>
                  <Text strong align="center">
                    {t(localizationMapping(value))}
                  </Text>
                </Box>
              );
            })}
          </Row>
          <Row background="#FFF">
            <Box padding="0 16px" width="15%" justifyContent="flex-start" background="#F2F2F2F2">
              <Text strong>{t(title)}</Text>
            </Box>
            {evaluationValues.map((value) => {
              const percentageGroup1 = getValuePercentagePerGroup(patientsGroup1, title, value);
              const percentageGroup2 = getValuePercentagePerGroup(patientsGroup2, title, value);
              return (
                <Box key={value}>
                  <Text width="40px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                    {percentageGroup1}
                  </Text>
                  {isGroup2Enabled && (
                    <>
                      <Separator background="#808080" />
                      <Text width="40px" color="#F15A24" align="left">
                        {percentageGroup2}
                      </Text>
                    </>
                  )}
                </Box>
              );
            })}
          </Row>
        </EvaluationDataDiv>
      );
    });
  };

  // Set P-Values
  useEffect(() => {
    const getEvaluationKey = (patientsGroup, key) => patientsGroup.map((evaluation) => Number(evaluation[key]));
    const stats = new StatisticsModule();

    if (!isGroup2Enabled && patientsGroup1.length) {
      const evaluationsPREGroup1 = getEvaluationsPerGroupAndPosition(patientsGroup1, 0);
      const evaluationsPOSTGroup1 = getEvaluationsPerGroupAndPosition(patientsGroup1, 1);

      const singleGroupData = {
        PRE: {
          Sleep: getEvaluationKey(evaluationsPREGroup1, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPREGroup1, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPREGroup1, "Pain"),
          Worry: getEvaluationKey(evaluationsPREGroup1, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPREGroup1, "DepressiveSymptoms"),
        },
        POST: {
          Sleep: getEvaluationKey(evaluationsPOSTGroup1, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPOSTGroup1, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPOSTGroup1, "Pain"),
          Worry: getEvaluationKey(evaluationsPOSTGroup1, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPOSTGroup1, "DepressiveSymptoms"),
        },
      };

      const singleGroupResults = stats.singleGroupAnalysis(singleGroupData);
      setPValues(singleGroupResults);
    }

    if (isGroup2Enabled && (patientsGroup1.length || patientsGroup2.length)) {
      const evaluationsPREGroup1 = getEvaluationsPerGroupAndPosition(patientsGroup1, 0);
      const evaluationsPREGroup2 = getEvaluationsPerGroupAndPosition(patientsGroup2, 0);
      const evaluationsPOSTGroup1 = getEvaluationsPerGroupAndPosition(patientsGroup1, 1);
      const evaluationsPOSTGroup2 = getEvaluationsPerGroupAndPosition(patientsGroup2, 1);

      const groupAData = {
        PRE: {
          Sleep: getEvaluationKey(evaluationsPREGroup1, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPREGroup1, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPREGroup1, "Pain"),
          Worry: getEvaluationKey(evaluationsPREGroup1, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPREGroup1, "DepressiveSymptoms"),
        },
        POST: {
          Sleep: getEvaluationKey(evaluationsPOSTGroup1, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPOSTGroup1, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPOSTGroup1, "Pain"),
          Worry: getEvaluationKey(evaluationsPOSTGroup1, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPOSTGroup1, "DepressiveSymptoms"),
        },
      };

      const groupBData = {
        PRE: {
          Sleep: getEvaluationKey(evaluationsPREGroup2, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPREGroup2, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPREGroup2, "Pain"),
          Worry: getEvaluationKey(evaluationsPREGroup2, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPREGroup2, "DepressiveSymptoms"),
        },
        POST: {
          Sleep: getEvaluationKey(evaluationsPOSTGroup2, "Sleep"),
          Dyspnoea: getEvaluationKey(evaluationsPOSTGroup2, "Dyspnoea"),
          Pain: getEvaluationKey(evaluationsPOSTGroup2, "Pain"),
          Worry: getEvaluationKey(evaluationsPOSTGroup2, "AnxiousSymptomatology"),
          Sadness: getEvaluationKey(evaluationsPOSTGroup2, "DepressiveSymptoms"),
        },
      };

      const twoGroupResults = stats.twoGroupAnalysis(groupAData, groupBData);
      setPValues(twoGroupResults);
    }

    return () => {
      setPValues({});
    };
  }, [isGroup2Enabled, patientsGroup1, patientsGroup2]);

  return (
    <>
      {isLoadingPatients ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={"icu"} />
          <MenuNavigation />
          <Title title={t("comparison").toUpperCase()} marginBottom="10px" />
          <DownloadReportPDF
            view={t("comparison")}
            showUser={false}
            plotsRef={{
              activitiesRef,
              evaluationsRef,
            }}
          />
          <ScreenshotContainer ref={activitiesRef}>
            {renderGroupForms()}
            {renderProtocolData("activities", activityIds, true)}
            {renderProtocolData("island", islandId, false)}
            {renderProtocolData("capsules", capsuleIds, false)}
          </ScreenshotContainer>
          <ScreenshotContainer ref={evaluationsRef}>
            <EvaluationsHeader />
            {renderSleepEvaluation()}
            {renderEvaluationsDataAverage()}
            {renderRASSEvaluation()}
            {renderEvaluationsDataPercentage()}
          </ScreenshotContainer>
        </>
      )}
    </>
  );
};

export default Comparison;
